import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FileUp, Keyboard, ScanLine } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { fmtDateTime } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import { listForms, type TallyForm } from './api'
import { FormStatusChip, formLabel, seatLabel } from './vocab'
import { FormWizard } from './forms/FormWizard'
import { ManualEntry } from './forms/ManualEntry'

/**
 * The paper: what has been uploaded, what has been read, what has been committed.
 *
 * ## A form and a return are different things
 *
 * The form is the evidence; the return is the claim about it. A form can sit here read but
 * uncommitted — nobody has agreed with the machine yet — and a return can exist with no form
 * at all, which is the ordinary case when an agent phones figures in from a station whose
 * scanner is down.
 *
 * ## Two roads, one destination
 *
 * **Upload a scan** goes through the machine and a field-by-field review. **Key in figures**
 * skips both and writes the same return directly. They differ in exactly one recorded thing —
 * `sourceCode`, `FORM_OCR` against `AGENT_ENTRY`, which is the first question asked when a
 * figure turns out to be wrong — and in nothing else: both land at `SUBMITTED`, both need a
 * second person to verify, both are corrected by supersession.
 *
 * Keying is not a degraded path. On the night, most stations phone or radio their figures in
 * long before any paper is scanned, and a system that only accepts scans is a system that has
 * no numbers until the morning.
 */
export function FormsPage() {
  const { electionId } = useElectionContext()
  const [params, setParams] = useSearchParams()
  const [rows, setRows] = useState<TallyForm[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const openId = params.get('form')
  const wizardOpen = params.get('upload') === '1' || openId !== null
  const manualOpen = params.get('key') === '1'

  const load = useCallback(async () => {
    if (!electionId) { setRows([]); return }
    try {
      setError(null)
      setRows(await listForms({ electionId, size: 200 }))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [electionId])

  useEffect(() => { void load() }, [load])

  if (!electionId) return <NoElectionSelected what="A results form" />

  const close = () => setParams((p) => {
    p.delete('upload'); p.delete('form'); p.delete('key'); return p
  }, { replace: true })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-erp-base font-semibold">Results forms</h2>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            Upload the form and let a machine read it, or key the figures straight in. Either
            way a second person verifies against the paper before anything counts.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button"
            onClick={() => setParams((p) => { p.set('key', '1'); return p }, { replace: true })}
            className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
            <Keyboard className="h-3.5 w-3.5" /> Key in figures
          </button>
          <button type="button"
            onClick={() => setParams((p) => { p.set('upload', '1'); return p }, { replace: true })}
            className="erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2">
            <FileUp className="h-3.5 w-3.5" /> Upload a form
          </button>
        </div>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />

      {rows !== null && rows.length === 0 && !error && (
        <EmptyState
          title="No forms uploaded yet"
          hint="A results form is a photo or scan of a Form 34A and its equivalents. The image
                is the evidence a disputed number is settled against, so it is worth uploading
                even when the figures are keyed in by hand." />
      )}

      {(rows === null || rows.length > 0) && (
        <ErpTable head={(
          <>
            <Th>Form</Th>
            <Th>Race</Th>
            <Th>Place</Th>
            <Th>Status</Th>
            <Th className="text-right">Confidence</Th>
            <Th>Uploaded</Th>
            <Th />
          </>
        )}>
          {rows === null ? <LoadingRows colSpan={7} />
            : rows.map((f) => (
              <tr key={f.id}>
                <Td>
                  <span className="inline-flex items-center gap-1.5">
                    <ScanLine className="h-3 w-3 text-muted-foreground" />
                    {formLabel(f.formTypeCode)}
                  </span>
                </Td>
                <Td>{seatLabel(f.seatTypeCode)}</Td>
                <Td>{f.unitName ?? '—'}</Td>
                <Td><FormStatusChip code={f.statusCode} /></Td>
                <Td className="text-right tabular-nums">
                  {f.meanConfidence == null ? '—'
                    : `${Math.round(f.meanConfidence * 100)}%`}
                </Td>
                <Td className="text-erp-xs text-muted-foreground">
                  {fmtDateTime(f.uploadedAt)}
                </Td>
                <Td>
                  <button type="button"
                    onClick={() => setParams((p) => {
                      p.set('form', f.id); return p
                    }, { replace: true })}
                    className="text-erp-xs text-chocolate hover:underline">
                    {f.statusCode === 'COMMITTED' ? 'View' : 'Review'}
                  </button>
                </Td>
              </tr>
            ))}
        </ErpTable>
      )}

      <Note tone="info">
        Committing a form creates a return at <strong>entered</strong>. It still needs a second
        person to verify it against the paper before it counts towards any board — and every
        number the reviewer changed from what the machine read is recorded as a disagreement.
      </Note>

      <ManualEntry
        open={manualOpen}
        electionId={electionId}
        onClose={close}
        onDone={load} />

      <FormWizard
        open={wizardOpen}
        formId={openId}
        electionId={electionId}
        onClose={close}
        onDone={async () => { close(); await load() }}
      />
    </div>
  )
}
